import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  useTheme,
  Skeleton,
  Grow,
  alpha,
} from '@mui/material';
import {
  Refresh,
  AccountBalanceWallet,
} from '@mui/icons-material';

interface UserInfo {
  companyName: string;
}

interface BalancesProps {
  wallets: any;
  userInfo: UserInfo;
  onRefreshBalance?: () => void;
  isLoading?: boolean;
}

// Gold Icon SVG Component
const GoldIcon = (props: any) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{
      width: 35,
      height: 35,
      fill: 'currentColor',
      ...props.sx
    }}
    {...props}
  >
    <path d="M8,11h8a1,1,0,0,0,.77-.37A1,1,0,0,0,17,9.8l-1-5A1,1,0,0,0,15,4H9a1,1,0,0,0-1,.8l-1,5a1,1,0,0,0,.21.83A1,1,0,0,0,8,11ZM9.82,6h4.36l.6,3H9.22ZM22,13.8a1,1,0,0,0-1-.8H15a1,1,0,0,0-1,.8l-1,5a1,1,0,0,0,.21.83A1,1,0,0,0,14,20h8a1,1,0,0,0,.77-.37A1,1,0,0,0,23,18.8ZM15.22,18l.6-3h4.36l.6,3ZM9,13H3a1,1,0,0,0-1,.8l-1,5a1,1,0,0,0,.21.83A1,1,0,0,0,2,20h8a1,1,0,0,0,.77-.37A1,1,0,0,0,11,18.8l-1-5A1,1,0,0,0,9,13ZM3.22,18l.6-3H8.18l.6,3Z" />
  </Box>
);

const Balances = ({
  wallets = [],
  userInfo = { companyName: '' },
  onRefreshBalance,
  isLoading = false
}: BalancesProps) => {
  const theme = useTheme();

  const getCurrencySymbol = (currencyId: string) => {
    switch (currencyId.toString()) {
      case '1':
        return '₺';
      case '2':
        return '$';
      case '3':
        return '€';
      case '4':
        return <GoldIcon sx={{ width: 28, height: 28 }} />;
      default:
        return '';
    }
  };

  const formatBalance = (balance: number) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(balance);
  };

  return (
    <Grid container spacing={2}>
      {isLoading ? (
        // Loading skeleton cards
        Array.from(new Array(4)).map((_, index) => (
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={`skeleton-${index}`}>
            <Grow in={true} timeout={600 + index * 200}>
              <Card
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  height: '100%',
                  minHeight: '180px',
                  borderRadius: '10px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: '25px 25px 12px 25px !important' }}>
                  <Box sx={{ width: '80%' }}>
                    <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="50%" height={20} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="70%" height={30} />
                  </Box>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        ))
      ) : (
        wallets.map((balance: any, index: number) => (
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={balance.id}>
            <Grow in={true} timeout={800 + index * 150}>
              <Card
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  height: '100%',
                  minHeight: '180px',
                  borderRadius: '10px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                  boxShadow: 'none',
                }}
              >
                <CardContent
                  sx={{
                    p: '25px 25px 12px 25px !important',
                    height: '100%',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', height: '100%' }}>
                    {/* Left Content Area */}
                    <Box sx={{ 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      position: 'relative',
                      zIndex: 1,
                      pl: { xs: 2, sm: 1 } // Add padding left to avoid overlap with decorative circle if needed, or just keep text on top
                    }}>
                      {/* Header Container */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.8, md: 1 } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '15px', md: '16px' },
                            color: theme.palette.text.primary,
                            lineHeight: 1.5,
                          }}
                        >
                          {balance.name}
                        </Typography>

                        {/* Refresh Button */}
                        {wallets.length > 0 && balance.currencyId == '1' && (
                          <IconButton
                            onClick={onRefreshBalance}
                            size="small"
                            sx={{
                              ml: 1,
                              p: 0.5,
                              color: 'primary.main',
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                transform: 'rotate(180deg)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <Refresh sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </Box>

                      {/* Details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '100%' }}>
                        {/* Wallet No */}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: '13px', sm: '14px' },
                            color: theme.palette.text.secondary,
                            fontWeight: 400,
                            lineHeight: 1.5,
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 600, color: 'black' }}>
                            Hesap No:
                          </Box>{' '}
                          {balance.walletNo}
                        </Typography>

                        {/* IBAN */}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: '13px', sm: '14px' },
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            width: '100%',
                            display: 'block',
                            position: 'relative',
                            zIndex: 10,
                            lineHeight: 1.5,
                          }}
                          title={balance.iban}
                        >
                          <Box component="span" sx={{ fontWeight: 600, color: 'black' }}>
                            IBAN:
                          </Box>{' '}
                          {balance.iban}
                        </Typography>

                        {/* Account Name */}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: '13px', sm: '14px' },
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: '100%',
                            display: 'block',
                            lineHeight: 1.5,
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 600, color: 'black' }}>
                            Hesap Adı:
                          </Box>{' '}
                          {userInfo.companyName}
                        </Typography>
                      </Box>

                      {/* Balance */}
                      <Typography
                        variant="h4"
                        sx={{
                          mb: 0,
                          mt: 1,
                          fontSize: { xs: '24px', sm: '26px', lg: '30px' },
                          fontWeight: 600,
                          lineHeight: 'normal',
                          color: theme.palette.text.primary,
                        }}
                      >
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {typeof getCurrencySymbol(balance.currencyId) === 'string' ? (
                            <Box component="span">{getCurrencySymbol(balance.currencyId)}</Box>
                          ) : (
                            getCurrencySymbol(balance.currencyId)
                          )}
                          <Box component="span">{formatBalance(balance.balance)}</Box>
                        </Box>
                      </Typography>
                    </Box>

                    {/* Right Bottom Decorative Area */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: { xs: '-20px', sm: '-30px' },
                        right: { xs: '-20px', sm: '-30px' },
                        width: { xs: '110px', sm: '130px' },
                        height: { xs: '110px', sm: '130px' },
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        zIndex: 0,
                      }}
                    >
                      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        
                        {/* Money Bag Icon */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          color: 'primary.main',
                          position: 'absolute',
                          top: '35%',
                          left: '35%',
                        }}>
                          <AccountBalanceWallet
                            sx={{
                              fontSize: { xs: 20, md: 26 },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Refresh Button Removed from here */}
                  </Box>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        ))
      )}
    </Grid>
  );
};

export default Balances;